import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CONSENT_MAPPING } from '../components/signup/consentQuestionsSimple';
import SimpleConsentForm from '../components/signup/SimpleConsentForm';

export default function ParentConsentApproval() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const childId = searchParams.get('childId');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [childName, setChildName] = useState('');
  const [consent, setConsent] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token || !childId) {
      setError('Invalid approval link. Please check your email and try again.');
      setLoading(false);
      return;
    }

    // Initialize consent with simplified mapping
    setConsent(CONSENT_MAPPING);

    // Validate token and get child info
    const validateToken = async () => {
      try {
        const functionsBase = process.env.REACT_APP_FUNCTIONS_BASE || 'https://us-central1-artmosphere-89b64.cloudfunctions.net';
        const res = await fetch(`${functionsBase}/validateConsentToken`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, childId })
        });

        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || 'Token validation failed');
        }

        setChildName(data.childName || 'Your child');
        setLoading(false);
      } catch (err) {
        console.error('Token validation error:', err);
        setError(err.message || 'Failed to validate approval link. Please check your email and try again.');
        setLoading(false);
      }
    };

    validateToken();
  }, [token, childId]);

  const handleConsentAgree = async (consentData) => {
    if (!token || !childId) {
      setError('Invalid approval link.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const functionsBase = process.env.REACT_APP_FUNCTIONS_BASE || 'https://us-central1-artmosphere-89b64.cloudfunctions.net';
      
      // Call backend to approve consent
      const res = await fetch(`${functionsBase}/approveConsentFromEmail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          childId,
          consent: consentData
        })
      });

      const data = await res.json();
      if (!res.ok) {
        // Handle specific error cases
        if (data.error && (data.error.includes('already been used') || data.error.includes('already approved'))) {
          // Token was already used but consent was already approved - show success
          if (data.alreadyApproved) {
            setSuccess(true);
            setError(null);
            setConsent(consentData);
            setTimeout(() => {
              navigate('/membership');
            }, 3000);
            return;
          }
          // If backend didn't confirm approval, still show success if error mentions "already"
          setSuccess(true);
          setError(null);
          setConsent(consentData);
          setTimeout(() => {
            navigate('/membership');
          }, 3000);
          return;
        }
        throw new Error(data.error || 'Failed to approve consent');
      }
      
      // Check if backend returned alreadyApproved flag
      if (data.alreadyApproved) {
        setSuccess(true);
        setError(null);
        setConsent(consentData);
        setTimeout(() => {
          navigate('/membership');
        }, 3000);
        return;
      }

      // Backend already saves to consentSettings collection, no need to save again from client
      setConsent(consentData);
      setSuccess(true);
      
      // Redirect to membership page after 3 seconds
      setTimeout(() => {
        navigate('/membership');
      }, 3000);
    } catch (err) {
      console.error('Approval error:', err);
      setError(err.message || 'Failed to approve consent. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 shadow-xl max-w-md w-full text-center">
          <div className="animate-spin h-12 w-12 border-4 border-amber-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-amber-800 font-semibold">Validating approval link...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 shadow-xl max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-green-600 text-3xl">✅</span>
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Consent Approved!</h2>
          <p className="text-slate-600 mb-6">
            Privacy settings for <strong>{childName}</strong> have been successfully approved and saved.
          </p>
          <p className="text-sm text-slate-500 mb-6">
            Redirecting to membership plans...
          </p>
        </div>
      </div>
    );
  }

  if (error && !success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 shadow-xl max-w-md w-full">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-red-600 text-2xl">⚠️</span>
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-4">Error</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-semibold transition-all"
            >
              Go to Artist Corner
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 flex items-center justify-center p-4">
      {!loading && !error && (
        <SimpleConsentForm
          onAgree={handleConsentAgree}
          onCancel={() => navigate('/')}
          isSubmitting={submitting}
        />
      )}
    </div>
  );
}
